import { Injectable, inject, signal } from '@angular/core';
import { DialogService } from '@tvd/design-system/dialog';
import type { ActiveDialog } from '@tvd/design-system/dialog';
import { TokenApiService } from '@tvd/data-access/token';
import { AiChatService } from './ai-chat.service';
import { firstValueFrom } from 'rxjs';

export interface AiChatDrawerConfig {
  orgId: string;
  shopId: string;
}

@Injectable({ providedIn: 'root' })
export class AiChatDrawerService {
  readonly #dialog = inject(DialogService);
  readonly #chatService = inject(AiChatService);
  readonly #tokenApi = inject(TokenApiService);
  #activeDialog: ActiveDialog<unknown> | undefined;
  readonly noTokens = signal(false);

  readonly open = async (config: AiChatDrawerConfig): Promise<void> => {
    // If already open, just bring to front
    if (this.#activeDialog) return;

    const { AiChatDrawer } = await import('./ai-chat-drawer');

    // Check token balance (non-blocking — show drawer immediately, update state async)
    firstValueFrom(this.#tokenApi.getTokenStatus())
      .then((status) => {
        this.noTokens.set(!status.hasAiPlan || status.totalBalance <= 0);
      })
      .catch(() => {});

    // Fetch existing sessions first, reuse the most recent one
    let session = this.#chatService.currentSession();
    if (!session) {
      try {
        await this.#chatService.fetchSessions();
        const existing = this.#chatService.sessions();
        if (existing.length > 0) {
          session = existing[0]; // Most recent
          this.#chatService.currentSession.set(session);
        } else {
          session = await this.#chatService.createSession(
            config.orgId,
            config.shopId,
          );
          this.#chatService.currentSession.set(session);
        }
      } catch {
        return;
      }
    }

    // Connect WebSocket
    this.#chatService.connect(session.id, config.orgId, config.shopId);

    const ref = this.#dialog
      .create(AiChatDrawer, {
        inputs: {
          orgId: () => config.orgId,
          shopId: () => config.shopId,
          noTokens: () => this.noTokens(),
        },
        outputs: {
          close: () => this.close(),
        },
      })
      .asDrawer({
        size: 'large',
        position: 'right',
        stickyContent: true,
      })
      .open();

    this.#activeDialog = ref;

    // Listen to native dialog close event — fires on Escape, backdrop click, or programmatic close
    ref.nativeElement.addEventListener(
      'close',
      () => {
        this.#chatService.disconnect();
        this.#activeDialog = undefined;
      },
      { once: true },
    );
  };

  readonly close = (): void => {
    if (this.#activeDialog) {
      this.#activeDialog.close();
      // Cleanup handled by native close event listener
    }
  };

  get isOpen(): boolean {
    return this.#activeDialog !== undefined;
  }
}
